// src/utils/staffAvailabilityHelper.js
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Get Monday of the week containing a given date
 * @param {Date} date
 * @returns {Date} Monday at midnight
 */
const getMondayOf = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Format a Date to MM-DD-YYYY (Firestore week doc key)
 * @param {Date} date
 * @returns {string}
 */
const toKey = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}-${d}-${y}`;
};

/**
 * Get the day-of-week id (monday, tuesday...) from a MM-DD-YYYY date string
 * @param {string} dateString - MM-DD-YYYY
 * @returns {string} e.g. 'monday'
 */
const getDayId = (dateString) => {
  const [month, day, year] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
};

/**
 * Availability status for one day, from an already-loaded week doc (or null if none submitted).
 * Synchronous — lets the scheduler work from live (onSnapshot) data.
 *
 * @param {Object|null} weekData - staff_weekly_availability/{staffId}/weeks/{weekKey} data
 * @param {string} dayId - 'monday', 'tuesday', ...
 * @returns {{ available: boolean, status: string, reason: string, startTime?: string, endTime?: string }}
 */
export const getDayAvailabilityStatus = (weekData, dayId) => {
  if (!weekData) {
    return {
      available: false,
      status: 'no-data',
      reason: 'No availability submitted for this week',
    };
  }

  const dayData = weekData.days?.[dayId];

  if (!dayData) {
    return {
      available: false,
      status: 'no-data',
      reason: 'No availability for this day',
    };
  }

  if (!dayData.available) {
    return {
      available: false,
      status: 'unavailable',
      reason: `Requested ${capitalize(dayId)} off`,
    };
  }

  return {
    available: true,
    status: 'available',
    reason: formatTimeRange(dayData.startTime, dayData.endTime),
    startTime: dayData.startTime || '',
    endTime: dayData.endTime || '',
  };
};

/**
 * Check whether a shift conflicts with submitted availability.
 * No submission = no conflict (shown separately as a neutral "no availability" marker).
 *
 * @param {{ startTime?: string, endTime?: string }} shift - times like "9:00 AM" or "09:00"
 * @param {Object|null} weekData - the staff member's week availability doc
 * @param {string} dayId - 'monday', 'tuesday', ...
 * @returns {string|null} human-readable reason, or null if no conflict
 */
export const getShiftConflict = (shift, weekData, dayId) => {
  const status = getDayAvailabilityStatus(weekData, dayId);
  if (status.status === 'unavailable') return status.reason;
  if (status.status !== 'available') return null;

  const shiftStart = parseTimeToMinutes(shift.startTime);
  const shiftEnd = parseTimeToMinutes(shift.endTime);
  const availStart = parseTimeToMinutes(status.startTime);
  const availEnd = parseTimeToMinutes(status.endTime);

  const startsEarly = shiftStart !== null && availStart !== null && shiftStart < availStart;
  const endsLate = shiftEnd !== null && availEnd !== null && shiftEnd > availEnd;
  if (!startsEarly && !endsLate) return null;

  const shiftRange = formatTimeRange(minutesTo24(shiftStart), minutesTo24(shiftEnd));
  return `Shift ${shiftRange} is outside availability ${status.reason}`;
};

/**
 * Check if a staff member is available on a specific date
 * Reads from staff_weekly_availability/{staffId}/weeks/{weekKey}
 *
 * @param {string} staffId
 * @param {string} dateString - MM-DD-YYYY
 * @returns {Promise<{ available: boolean, status: string, reason: string, startTime?: string, endTime?: string }>}
 */
export const checkStaffAvailability = async (staffId, dateString) => {
  try {
    const [month, day, year] = dateString.split('-').map(Number);
    const checkDate = new Date(year, month - 1, day);
    const weekMonday = getMondayOf(checkDate);
    const weekKey = toKey(weekMonday);
    const dayId = getDayId(dateString);

    const ref = doc(db, 'staff_weekly_availability', staffId, 'weeks', weekKey);
    const snap = await getDoc(ref);

    return getDayAvailabilityStatus(snap.exists() ? snap.data() : null, dayId);
  } catch (err) {
    console.error('Error checking staff availability:', err);
    return {
      available: false,
      status: 'error',
      reason: 'Could not load availability',
    };
  }
};

/**
 * Get full week availability for a staff member
 * @param {string} staffId
 * @param {string} weekKey - MM-DD-YYYY of the Monday
 * @returns {Promise<Object|null>} week data or null
 */
export const getStaffWeekAvailability = async (staffId, weekKey) => {
  try {
    const ref = doc(db, 'staff_weekly_availability', staffId, 'weeks', weekKey);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.error('Error fetching week availability:', err);
    return null;
  }
};

/**
 * Get availability for all staff for a given week
 * @param {Array} staffList - Array of staff objects with .id
 * @param {string} weekKey - MM-DD-YYYY of Monday
 * @returns {Promise<Object>} { staffId: weekData }
 */
export const getAllStaffWeekAvailability = async (staffList, weekKey) => {
  const results = {};
  await Promise.all(
    staffList.map(async (staff) => {
      results[staff.id] = await getStaffWeekAvailability(staff.id, weekKey);
    })
  );
  return results;
};

/**
 * Get available staff for a specific date
 * @param {Array} staffList
 * @param {string} dateString - MM-DD-YYYY
 * @returns {Promise<Array>} Staff members with availability info, available ones first
 */
export const getAvailableStaff = async (staffList, dateString) => {
  const results = await Promise.all(
    staffList
      .filter(s => s.active)
      .map(async (staff) => {
        const availCheck = await checkStaffAvailability(staff.id, dateString);
        return { ...staff, availabilityCheck: availCheck };
      })
  );

  return results.sort((a, b) => {
    // Available first
    if (a.availabilityCheck.available && !b.availabilityCheck.available) return -1;
    if (!a.availabilityCheck.available && b.availabilityCheck.available) return 1;
    // Then alphabetical
    const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
    const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

/**
 * Get visual indicator for availability status
 * @param {string} status
 * @returns {string} emoji
 */
export const getAvailabilityIndicator = (status) => {
  switch (status) {
    case 'available':    return '✅';
    case 'unavailable':  return '🔴';
    case 'no-data':      return '⚪';
    case 'error':        return '❓';
    default:             return '❓';
  }
};

/**
 * Format MM-DD-YYYY date string to week key (Monday of that week)
 * Useful for scheduler lookups
 * @param {string} dateString - MM-DD-YYYY
 * @returns {string} MM-DD-YYYY of Monday
 */
export const getWeekKeyForDate = (dateString) => {
  const [month, day, year] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return toKey(getMondayOf(date));
};

// ── Helpers ──────────────────────────────────────────────

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

// Parse "9:00 AM", "9:00am" or "09:00" into minutes since midnight (null if unparseable)
const parseTimeToMinutes = (t) => {
  if (!t) return null;
  const match = t.trim().toUpperCase().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
  if (!match) return null;
  let h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (match[3] === 'PM' && h !== 12) h += 12;
  if (match[3] === 'AM' && h === 12) h = 0;
  return h * 60 + m;
};

const minutesTo24 = (mins) => {
  if (mins === null) return '';
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
};

const to12Hour = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')}${ampm}`;
};

const formatTimeRange = (startTime, endTime) => {
  if (!startTime && !endTime) return 'Available (no hours specified)';
  if (startTime && endTime) return `${to12Hour(startTime)} – ${to12Hour(endTime)}`;
  if (startTime) return `From ${to12Hour(startTime)}`;
  return `Until ${to12Hour(endTime)}`;
};